import { EditorProvider } from "@tiptap/react";
import { useDebounce } from "@utils/hooks";
import { useUnsavedChanges } from "@providers/unsavedChanges";
import React, { useEffect, useId, useMemo, useState } from "react";
import ExtensionKit from "./extensions/extension-kit";
import { useBlockEditor as useRichTextEditor } from "./hooks";
import { TextMenu } from "./menus";

export const RichTextEditor = (props: {
  initialContent: string;
  onSaveContent?: (content: string) => void;
  onChange?: (content: string) => void;
  onClose?: () => void;
  includeMenu?: boolean;
  editable?: boolean;
  defaultAlignment?: "justify" | "center" | "left" | "right";
  showAITools?: boolean;
  showFontControls?: boolean;
  aiEditorSettings?: {
    id: string;
    nodeType: "exhibit" | "module";
  };
}) => {
  const { editor } = useRichTextEditor(props);
  const [content, setContent] = useState(props.initialContent);
  const deboucedContent = useDebounce(content, 1000);
  // Default to true
  const includeMenu =
    typeof props.includeMenu === "boolean" ? props.includeMenu : true;

  useEffect(() => {
    if (props.onSaveContent) {
      props.onSaveContent(deboucedContent);
    }
  }, [deboucedContent, props]);

  // Flag this editor as having unsaved changes while a keystroke is still ahead
  // of the debounced value that gets persisted. Drives the exit confirmation.
  const { markDirty, markClean } = useUnsavedChanges();
  const dirtyKey = useId();
  useEffect(() => {
    if (content !== deboucedContent) {
      markDirty(dirtyKey);
    } else {
      markClean(dirtyKey);
    }
  }, [content, deboucedContent, dirtyKey, markDirty, markClean]);
  // Clear on unmount so a closed editor never leaves a stale dirty flag.
  useEffect(() => () => markClean(dirtyKey), [dirtyKey, markClean]);

  const memoizedTextMenu = useMemo(() => {
    return (
      <TextMenu
        onClose={props.onClose}
        showAITools={props.showAITools}
        showFontControls={props.showFontControls}
      />
    );
  }, [props.onClose, props.showAITools, props.showFontControls]);

  if (!editor) {
    return null;
  }

  const extensionSettings = {
    defaultAlignment: props.defaultAlignment,
  };

  return (
    <EditorProvider
      extensions={[
        ...ExtensionKit(
          props.aiEditorSettings
            ? { ...props.aiEditorSettings, ...extensionSettings }
            : extensionSettings
        ),
      ]}
      content={props.initialContent}
      slotBefore={includeMenu ? memoizedTextMenu : undefined}
      onUpdate={({ editor }) => {
        const newContent = editor.getHTML();
        if (props.onSaveContent) {
          setContent(newContent);
        }
        if (props.onChange) {
          props.onChange(newContent);
        }
      }}
      editable={props.editable}
    />
  );
};

const MemoizedRichTextEditor = React.memo(RichTextEditor);
export default MemoizedRichTextEditor;
